// 加入我们数据标准化
import { resolveFileUrl } from '@/components/shared/utils/fileUrl'
import { resolveBodyHtml } from '@/components/shared/utils/resolveBodyHtml'
import { normalizeContentAttachments } from '@/components/shared/utils/contentAttachments'
import { parseShowAttachments } from '@/components/shared/utils/contentDisplayFlags'
import {
  getMenuNodePath,
  slugFromMenuPath
} from '@/components/portal/common/utils/menuPath'
import { resolveTemplateId } from '@/components/portal/common/utils/menuTemplateRegistry'
import { isPortalMenuVisible } from '@/components/portal/common/utils/portalMenuVisibility'

function findJoinMenu(menus = [], rootMenu = null) {
  if (rootMenu) return rootMenu
  return (menus || []).find((menu) => (
    isPortalMenuVisible(menu)
    && resolveTemplateId(menu.templateId) === 12
  )) || null
}

/** 收集加入我们菜单下的所有叶子节点 */
function collectJoinLeafMenus(joinMenu) {
  const leaves = []

  const walk = (nodes = [], parentPath = '') => {
    for (const node of nodes) {
      if (!isPortalMenuVisible(node)) continue
      const children = (node.children || []).filter((item) => isPortalMenuVisible(item))
      if (!children.length) {
        leaves.push({
          menuId: node.id,
          slug: slugFromMenuPath(node.path) || String(node.id),
          name: node.name || '',
          sortOrder: node.sortOrder ?? 0
        })
        continue
      }

      walk(children, slugFromMenuPath(node.path) || parentPath)
    }
  }

  if (!joinMenu) return leaves
  const children = (joinMenu.children || []).filter((item) => isPortalMenuVisible(item))
  if (!children.length) {
    leaves.push({
      menuId: joinMenu.id,
      slug: slugFromMenuPath(joinMenu.path) || String(joinMenu.id),
      name: joinMenu.name || '',
      sortOrder: joinMenu.sortOrder ?? 0
    })
    return leaves
  }

  walk(children)
  return leaves.sort((a, b) => a.sortOrder - b.sortOrder)
}

/** 构建加入我们 menuId → 元数据（含 section 类型）的映射表 */
export function buildJoinMenuMap(menus = [], rootMenu = null) {
  const map = new Map()
  const leaves = collectJoinLeafMenus(findJoinMenu(menus, rootMenu))

  leaves.forEach((leaf, index) => {
    map.set(leaf.menuId, {
      ...leaf,
      section: resolveJoinSection(leaf, index)
    })
  })

  return map
}

/** 根据 slug/名称推断加入我们的区块类型（philosophy / supplier） */
function resolveJoinSection(menuMeta, index = 0) {
  const slug = String(menuMeta.slug || '').toLowerCase()
  const name = String(menuMeta.name || '')

  if (/supplier|gongying|供应商/.test(slug) || name.includes('供应商')) {
    return 'supplier'
  }
  if (/philosophy|talent|rencai|理念/.test(slug) || name.includes('理念')) {
    return 'philosophy'
  }

  return index === 0 ? 'philosophy' : 'supplier'
}

/** 批量标准化加入我们内容列表 */
export function normalizeJoinItems(contents = [], menus = [], rootMenu = null) {
  const menuMap = buildJoinMenuMap(menus, rootMenu)
  const joinMenuIds = new Set(menuMap.keys())
  if (!joinMenuIds.size) return []

  return (contents || [])
    .filter((item) => item.type === 'INTRODUCTION' && joinMenuIds.has(item.menuId))
    .map((item) => {
      const menu = menuMap.get(item.menuId) || {}
      return {
        id: item.id,
        menuId: item.menuId,
        section: menu.section || 'philosophy',
        slug: menu.slug || String(item.menuId || item.id),
        title: item.title || menu.name || '',
        summary: item.summary || '',
        image: resolveFileUrl(item.coverUrl),
        content: resolveBodyHtml(item.bodyHtml, item.bodyJson),
        attachments: normalizeContentAttachments(item.attachments),
        showAttachments: parseShowAttachments(item),
        sortOrder: menu.sortOrder ?? item.sortOrder ?? 0
      }
    })
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

/** 标准化加入我们单个区块详情 */
export function normalizeJoinSection(content, listItem = null) {
  if (!content) return null

  return {
    id: content.id,
    menuId: content.menuId,
    section: listItem?.section || 'philosophy',
    title: content.title || listItem?.title || '',
    summary: content.summary || listItem?.summary || '',
    image: resolveFileUrl(content.coverUrl) || listItem?.image || '',
    content: resolveBodyHtml(content.bodyHtml, content.bodyJson),
    attachments: normalizeContentAttachments(content.attachments),
    showAttachments: parseShowAttachments(content),
    sortOrder: listItem?.sortOrder ?? content.sortOrder ?? 0
  }
}

/** 将加入我们的区块列表按 philosophy / supplier 分组 */
export function groupJoinSections(sections = []) {
  const grouped = {
    philosophy: null,
    supplier: null
  }

  for (const section of sections) {
    if (!section) continue
    if (section.section === 'supplier' && !grouped.supplier) {
      grouped.supplier = section
      continue
    }
    if (section.section === 'philosophy' && !grouped.philosophy) {
      grouped.philosophy = section
    }
  }

  if (!grouped.philosophy) {
    grouped.philosophy = sections.find((item) => item.section === 'philosophy') || sections[0] || null
  }
  if (!grouped.supplier) {
    grouped.supplier = sections.find((item) => item.section === 'supplier')
      || (sections.length > 1 ? sections[1] : null)
  }

  return grouped
}

/** 获取加入我们根路径 */
export function getJoinRootPath(menus = [], rootMenu = null) {
  return getMenuNodePath(findJoinMenu(menus, rootMenu))
}
